/**
 * GPU Marketplace API client service
 * Uses REACT_APP_MARKETPLACE_API_URL environment variable
 */

import axios, { AxiosInstance } from 'axios';
import {
  GPUProductResponse,
  GPUProductListResponse,
  InventoryResponse,
  InventoryListResponse,
  InventoryFilter,
  CategoryPrice,
  CostEstimate,
  CostEstimateRequest,
  OSImageResponse,
  OSImageListResponse,
  ReservationResponse,
  ReservationListResponse,
  CreateReservationRequest,
  ReservationFilter,
  SSHAccessInfo,
  CreateOrderRequest,
  CreateOrderResponse,
  VerifyPaymentRequest,
  VerifyPaymentResponse,
} from '../types/marketplace';

const MARKETPLACE_API_URL = process.env.REACT_APP_MARKETPLACE_API_URL || 'https://cloud.podstack.ai/marketplace/api/v1/marketplace';

const createApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token interceptor
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return client;
};

const apiClient = createApiClient(MARKETPLACE_API_URL);

export const marketplaceApi = {
  // ============================================
  // GPU Products
  // ============================================

  /**
   * List all available GPU products
   */
  async listGPUProducts(): Promise<GPUProductListResponse> {
    const response = await apiClient.get<GPUProductListResponse>('/gpu-products');
    return response.data;
  },

  /**
   * Get GPU product details by slug
   */
  async getGPUProduct(slug: string): Promise<GPUProductResponse> {
    const response = await apiClient.get<GPUProductResponse>(`/gpu-products/${slug}`);
    return response.data;
  },

  /**
   * Get inventory items for a specific GPU type
   */
  async getInventoryByGPUType(slug: string): Promise<InventoryListResponse> {
    const response = await apiClient.get<InventoryListResponse>(`/gpu-products/${slug}/inventory`);
    return response.data;
  },

  // ============================================
  // Inventory
  // ============================================

  /**
   * List available inventory with optional filters
   */
  async listInventory(filter?: InventoryFilter): Promise<InventoryListResponse> {
    const params: Record<string, string | number | boolean> = {};

    if (filter) {
      if (filter.gpuType?.length) params.gpuType = filter.gpuType.join(',');
      if (filter.region?.length) params.region = filter.region.join(',');
      if (filter.minGpuCount) params.minGpuCount = filter.minGpuCount;
      if (filter.maxGpuCount) params.maxGpuCount = filter.maxGpuCount;
      if (filter.minVram) params.minVram = filter.minVram;
      if (filter.maxPrice) params.maxPrice = filter.maxPrice;
      if (filter.available !== undefined) params.available = filter.available;
      if (filter.spotOnly !== undefined) params.spotOnly = filter.spotOnly;
      if (filter.sortBy) params.sortBy = filter.sortBy;
      if (filter.sortOrder) params.sortOrder = filter.sortOrder;
      if (filter.page) params.page = filter.page;
      if (filter.pageSize) params.pageSize = filter.pageSize;
    }

    const response = await apiClient.get<InventoryListResponse>('/inventory', { params });
    return response.data;
  },

  /**
   * Get inventory item details by ID
   */
  async getInventory(id: string): Promise<InventoryResponse> {
    const response = await apiClient.get<InventoryResponse>(`/inventory/${id}`);
    return response.data;
  },

  /**
   * Get pricing details for an inventory item
   */
  async getInventoryPricing(id: string): Promise<InventoryResponse> {
    const response = await apiClient.get<InventoryResponse>(`/inventory/${id}/pricing`);
    return response.data;
  },

  /**
   * Estimate cost for an inventory item (requires auth)
   */
  async estimateCost(id: string, request: CostEstimateRequest): Promise<CostEstimate> {
    const response = await apiClient.post<CostEstimate>(`/inventory/${id}/estimate`, request);
    return response.data;
  },

  // ============================================
  // Pricing
  // ============================================

  /**
   * Get category-level pricing (starting prices per GPU type)
   */
  async getCategoryPrices(): Promise<CategoryPrice[]> {
    const response = await apiClient.get<CategoryPrice[]>('/pricing/categories');
    return response.data;
  },

  // ============================================
  // OS Images
  // ============================================

  /**
   * List available OS images
   */
  async listOSImages(): Promise<OSImageListResponse> {
    const response = await apiClient.get<OSImageListResponse>('/os-images');
    return response.data;
  },

  /**
   * Get default OS image
   */
  async getDefaultOSImage(): Promise<OSImageResponse> {
    const response = await apiClient.get<OSImageResponse>('/os-images/default');
    return response.data;
  },

  /**
   * Get OS image details by slug
   */
  async getOSImage(slug: string): Promise<OSImageResponse> {
    const response = await apiClient.get<OSImageResponse>(`/os-images/${slug}`);
    return response.data;
  },

  // ============================================
  // Reservations (requires auth)
  // ============================================

  /**
   * Create a new reservation
   */
  async createReservation(request: CreateReservationRequest): Promise<ReservationResponse> {
    const response = await apiClient.post<ReservationResponse>('/reservations', request);
    return response.data;
  },

  /**
   * List user's reservations
   */
  async listReservations(filter?: ReservationFilter): Promise<ReservationListResponse> {
    const params: Record<string, string | number> = {};

    if (filter) {
      if (filter.status) params.status = filter.status;
      if (filter.page) params.page = filter.page;
      if (filter.pageSize) params.pageSize = filter.pageSize;
    }

    const response = await apiClient.get<ReservationListResponse>('/reservations', { params });
    return response.data;
  },

  /**
   * Get reservation details by ID
   */
  async getReservation(id: string): Promise<ReservationResponse> {
    const response = await apiClient.get<ReservationResponse>(`/reservations/${id}`);
    return response.data;
  },

  /**
   * Cancel a reservation
   */
  async cancelReservation(id: string): Promise<void> {
    await apiClient.delete(`/reservations/${id}`);
  },

  /**
   * Get SSH access details for a reservation
   */
  async getSSHAccess(id: string): Promise<SSHAccessInfo> {
    const response = await apiClient.get<SSHAccessInfo>(`/reservations/${id}/ssh`);
    return response.data;
  },

  // ============================================
  // Payment Orders (requires auth)
  // ============================================

  /**
   * Create a payment order for GPU reservation
   * Returns Razorpay order details for checkout
   */
  async createOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
    const response = await apiClient.post<CreateOrderResponse>('/orders', request);
    return response.data;
  },

  /**
   * Verify payment after Razorpay checkout completion
   */
  async verifyPayment(request: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
    const response = await apiClient.post<VerifyPaymentResponse>('/orders/verify', request);
    return response.data;
  },

  /**
   * List user's reservations with payment status
   */
  async listMyReservations(filter?: ReservationFilter): Promise<ReservationListResponse> {
    const params: Record<string, string | number> = {};

    if (filter) {
      if (filter.status) params.status = filter.status;
      if (filter.page) params.page = filter.page;
      if (filter.pageSize) params.pageSize = filter.pageSize;
    }

    const response = await apiClient.get<ReservationListResponse>('/my-reservations', { params });
    return response.data;
  },

  /**
   * Get reservation details by code
   */
  async getMyReservation(code: string): Promise<ReservationResponse> {
    const response = await apiClient.get<ReservationResponse>(`/my-reservations/${code}`);
    return response.data;
  },
};

export default marketplaceApi;
